import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import PhoneInput from 'react-phone-input-2';
import countriesData from '../../data/countriesData.json';
import 'react-phone-input-2/lib/style.css';
import Toast from '../../ui/Toast';
import './AdminDashboard.css';
import '../workspace/Members.css';
import Pagination from '../../ui/Pagination';
import OrgChartModal from './OrgChartModal';
import './OrganizationManagement.css';

const OrganizationManagement = ({ refreshProfile }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Search and Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageInput, setPageInput] = useState('1');

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ sortBy: 'name', sortOrder: 'asc' });

  const [formData, setFormData] = useState({
    name: '',
    firstName: '',
    lastName: '',
    email: '',
    companyRegistrationNumber: '',
    industry: '',
    country: '',
    phoneNumber: '',
    credits: 0,
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });

  const normalizePlan = (plan) => {
    const allowedPlans = ['Free', 'Bronze', 'Silver', 'Gold', 'Scale'];
    if (!plan) return 'Free';

    const value = String(plan).trim();
    if (!value) return 'Free';

    const canonical = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    return allowedPlans.includes(canonical) ? canonical : 'Free';
  };

  // Adjust Credits Modal State
  const [showAdjustCreditsModal, setShowAdjustCreditsModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [adjustCreditsAmount, setAdjustCreditsAmount] = useState('');

  // Org Chart Modal State
  const [showOrgChartModal, setShowOrgChartModal] = useState(false);
  const [selectedOrgForChart, setSelectedOrgForChart] = useState(null);

  const openOrgChartModal = (org) => {
    setSelectedOrgForChart(org);
    setShowOrgChartModal(true);
  };

  const closeOrgChartModal = () => {
    setShowOrgChartModal(false);
    setSelectedOrgForChart(null);
  };

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterPlan, itemsPerPage]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  // Fetch from the real API
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/organizations');
      if (response.data.success) {
        setOrganizations(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch orgs', error);
      setToast({ show: true, message: 'Failed to load organizations', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const openForm = () => {
    setFormData({
      name: '',
      firstName: '',
      lastName: '',
      email: '',
      companyRegistrationNumber: '',
      industry: '',
      country: '',
      phoneNumber: '',
      credits: 0,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
  };

  const openAdjustCreditsModal = (org) => {
    setSelectedOrg(org);
    setAdjustCreditsAmount(String(org.credits || 0));
    setShowAdjustCreditsModal(true);
  };

  const closeAdjustCreditsModal = () => {
    setShowAdjustCreditsModal(false);
    setSelectedOrg(null);
    setAdjustCreditsAmount('');
  };

  const handleAdjustCredits = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    try {
      const newAmount = parseInt(adjustCreditsAmount) || 0;
      const res = await axios.put(`/organizations/${selectedOrg.id}/credits`, {
        amount: newAmount,
        description: 'Manual adjustment by admin',
      });
      if (res.data.success) {
        setToast({ show: true, message: 'Credits adjusted successfully', type: 'success' });
        fetchOrganizations();
        if (refreshProfile) refreshProfile();
        closeAdjustCreditsModal();
      }
    } catch (error) {
      console.error('Failed to adjust credits', error);
      setToast({
        show: true,
        message: error.response?.data?.error || 'Failed to adjust credits',
        type: 'error',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        companyRegistrationNumber: formData.companyRegistrationNumber,
        industry: formData.industry,
        country: formData.country,
        phoneNumber: formData.phoneNumber,
        credits: parseInt(formData.credits) || 0,
      };

      const res = await axios.post('/organizations', payload);
      if (res.data.success) {
        setToast({ show: true, message: 'Organization created successfully', type: 'success' });
        fetchOrganizations();
      }
      closeForm();
    } catch (error) {
      console.error('Save failed', error);
      setToast({
        show: true,
        message: error.response?.data?.error || 'Failed to save organization',
        type: 'error',
      });
    }
  };

  // Filter logic
  const filteredOrgs = organizations.filter((org) => {
    const matchesSearch = org.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = filterPlan === 'all' || normalizePlan(org.plan) === filterPlan;

    return matchesSearch && matchesPlan;
  });

  const handleSort = (field) => {
    const isAsc = sortConfig.sortBy === field && sortConfig.sortOrder === 'asc';
    setSortConfig({
      sortBy: field,
      sortOrder: isAsc ? 'desc' : 'asc',
    });
    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortConfig.sortBy !== field)
      return (
        <i
          className="fas fa-sort"
          style={{ marginLeft: '4px', opacity: 0.3, fontSize: '10px' }}
        ></i>
      );
    return sortConfig.sortOrder === 'asc' ? (
      <i
        className="fas fa-sort-up"
        style={{ marginLeft: '4px', color: '#3b82f6', fontSize: '10px' }}
      ></i>
    ) : (
      <i
        className="fas fa-sort-down"
        style={{ marginLeft: '4px', color: '#3b82f6', fontSize: '10px' }}
      ></i>
    );
  };

  const getPlanBadgeStyle = (plan) => {
    const p = normalizePlan(plan);
    switch (p) {
      case 'Scale':
        return { background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe' };
      case 'Gold':
        return { background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' };
      case 'Silver':
        return { background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' };
      case 'Bronze':
        return { background: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5' };
      default: // Free
        return { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' };
    }
  };

  const sortedOrgs = [...filteredOrgs].sort((a, b) => {
    let aVal, bVal;

    switch (sortConfig.sortBy) {
      case 'name':
        aVal = String(a.name || '').toLowerCase();
        bVal = String(b.name || '').toLowerCase();
        break;
      case 'owner_email':
        aVal = String(a.owner_email || '').toLowerCase();
        bVal = String(b.owner_email || '').toLowerCase();
        break;
      case 'plan':
        aVal = normalizePlan(a.plan);
        bVal = normalizePlan(b.plan);
        break;
      case 'userCount':
        aVal = Number(a.userCount || 0);
        bVal = Number(b.userCount || 0);
        break;
      case 'credits':
        aVal = Number(a.credits || 0);
        bVal = Number(b.credits || 0);
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortConfig.sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sortedOrgs.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrgs = sortedOrgs.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="tm-page" style={{ padding: 0 }}>
      <div className="tm-toolbar">
        <div className="tm-toolbar-group tm-toolbar-grow">
          <label className="tm-toolbar-label">Search</label>
          <div className="tm-toolbar-search-wrap">
            <i className="fas fa-search tm-toolbar-search-icon"></i>
            <input
              className="tm-toolbar-input"
              type="text"
              placeholder="Search organizations by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="tm-toolbar-clear" onClick={() => setSearchTerm('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
        <div className="tm-toolbar-group">
          <label className="tm-toolbar-label">Plan</label>
          <select
            className="tm-toolbar-select"
            value={filterPlan}
            onChange={(e) => setFilterPlan(e.target.value)}
          >
            <option value="all">All Plans</option>
            <option value="Free">Free</option>
            <option value="Bronze">Bronze</option>
            <option value="Silver">Silver</option>
            <option value="Gold">Gold</option>
            <option value="Scale">Scale</option>
          </select>
        </div>
        <div className="tm-toolbar-group" style={{ minWidth: '118px', flex: '0 0 118px' }}>
          <label className="tm-toolbar-label" style={{ opacity: 0 }}>
            &nbsp;
          </label>
          <button
            className="tm-btn tm-btn-outline"
            style={{ width: '100%', justifyContent: 'center', height: '32px' }}
            onClick={() => {
              setSearchTerm('');
              setFilterPlan('all');
              setSortConfig({ sortBy: 'name', sortOrder: 'asc' });
              setCurrentPage(1);
            }}
          >
            <i className="fas fa-times"></i> Clear
          </button>
        </div>
        <div className="tm-toolbar-group" style={{ minWidth: '160px', flex: '0 0 160px' }}>
          <label className="tm-toolbar-label" style={{ opacity: 0 }}>
            &nbsp;
          </label>
          <button
            onClick={() => openForm()}
            className="tm-btn tm-btn-primary"
            style={{ width: '100%', justifyContent: 'center', height: '32px', gap: '6px' }}
          >
            <i className="fas fa-plus"></i> Add Organization
          </button>
        </div>
      </div>

      {/* Organizations Table with Integrated Pagination */}
      <Pagination
        columns={[
          { key: 'name', label: 'Organization', sortable: true, width: '220px' },
          { key: 'owner_email', label: 'Owner Email', sortable: true, width: '220px' },
          { key: 'plan', label: 'Plan', sortable: true, className: 'text-center', width: '120px' },
          {
            key: 'userCount',
            label: 'Users',
            sortable: true,
            className: 'text-center',
            width: '100px',
          },
          {
            key: 'credits',
            label: 'Credit Balance',
            sortable: true,
            className: 'text-center',
            width: '130px',
          },
          {
            key: 'actions',
            label: 'Actions',
            sortable: false,
            className: 'text-center',
            width: '120px',
          },
        ]}
        data={currentOrgs}
        onSort={handleSort}
        renderRow={(org) => (
          <tr key={org.id}>
            <td className="col-member">
              <div className="tm-member-cell">
                <div className="tm-avatar" style={{ background: '#3b82f6' }}>
                  {org.name?.substring(0, 2).toUpperCase() || 'OR'}
                </div>
                <div className="tm-member-info">
                  <span className="tm-name" style={{ color: '#0f172a' }}>
                    {org.name}
                  </span>
                </div>
              </div>
            </td>
            <td style={{ fontSize: '13px', color: '#445164' }}>{org.owner_email || '—'}</td>
            <td style={{ textAlign: 'center' }}>
              <span
                className="tm-role-pill"
                style={{
                  ...getPlanBadgeStyle(org.plan),
                  fontSize: '11px',
                  fontWeight: '700',
                  padding: '4px 12px',
                  textTransform: 'uppercase',
                }}
              >
                {normalizePlan(org.plan)}
              </span>
            </td>
            <td style={{ textAlign: 'center', color: '#445164', fontWeight: '500' }}>
              {org.userCount || 0}
            </td>
            <td style={{ textAlign: 'center' }}>
              <button
                onClick={() => openAdjustCreditsModal(org)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  padding: 0,
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
              >
                {org.credits?.toLocaleString() || 0}
              </button>
            </td>
            <td style={{ textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => openOrgChartModal(org)}
                className="tm-org-action-btn"
                title={`View Org Chart for ${org.name}`}
              >
                <i className="fas fa-sitemap"></i>
                <span>Org Chart</span>
              </button>
            </td>
          </tr>
        )}
        loading={loading}
        emptyState={
          <>
            <i className="fas fa-building-slash" />
            <p>No organizations found matching your criteria.</p>
          </>
        }
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={setItemsPerPage}
        totalItems={sortedOrgs.length}
        itemsName="organizations"
        className="tm-org-management-card"
      />

      {/* Add/Edit Organization Modal */}
      {showForm && (
        <div
          className="tm-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={(e) => e.target === e.currentTarget && closeForm()}
        >
          <div
            className="tm-modal animate-fade-in"
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              width: '600px',
              maxWidth: '90%',
              overflow: 'hidden',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div
              className="tm-modal-head"
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                <i className="fas fa-plus" style={{ marginRight: '8px', color: '#2563eb' }}></i>
                Create New Organization
              </h3>
              <button
                className="tm-modal-close"
                onClick={closeForm}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#64748b',
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ padding: '20px 24px', maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="admin-form-grid">
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label className="form-label">Organization Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. Acme Inc."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Owner First Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="John"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Owner Last Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Doe"
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label className="form-label">Owner Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john.doe@example.com"
                    />
                  </div>

                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label className="form-label">Company Registration Number *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={formData.companyRegistrationNumber}
                      onChange={(e) =>
                        setFormData({ ...formData, companyRegistrationNumber: e.target.value })
                      }
                      placeholder="Enter registration number"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Industry</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      placeholder="e.g., Technology, Finance"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Country *</label>
                    <select
                      className="form-input"
                      required
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    >
                      <option value="">Select your country</option>
                      {countriesData.map((c) => (
                        <option key={c.code} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label className="form-label">Phone Number</label>
                    <PhoneInput
                      country={
                        countriesData
                          .find((c) => c.name === formData.country)
                          ?.code?.toLowerCase() || 'my'
                      }
                      value={formData.phoneNumber}
                      onChange={(phone) => setFormData({ ...formData, phoneNumber: phone })}
                      disableDropdown={true}
                      inputProps={{
                        name: 'phoneNumber',
                        id: 'phoneNumber',
                        className: 'form-input',
                        style: { width: '100%' },
                      }}
                      containerStyle={{ width: '100%' }}
                      inputStyle={{ width: '100%', height: '42px' }}
                    />
                  </div>

                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label className="form-label">Credit Limit (Monthly)</label>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.credits}
                      onChange={(e) => setFormData({ ...formData, credits: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  padding: '16px 24px',
                  background: '#f8fafc',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                }}
              >
                <button type="button" className="tm-btn tm-btn-outline" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="tm-btn tm-btn-primary">
                  Create Organization
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adjust Credits Modal */}
      {showAdjustCreditsModal && selectedOrg && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={(e) => e.target === e.currentTarget && closeAdjustCreditsModal()}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              maxWidth: '500px',
              width: '90%',
            }}
          >
            <div
              style={{
                padding: '24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#0f172a' }}>
                Adjust Balance - {selectedOrg.name}
              </h2>
              <button
                onClick={closeAdjustCreditsModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '20px',
                  color: '#64748b',
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAdjustCredits} style={{ padding: '24px' }}>
              <div className="form-group">
                <label className="form-label">
                  Current Balance: {selectedOrg.credits?.toLocaleString() || 0}
                </label>
              </div>
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="form-label">New Amount *</label>
                <input
                  type="number"
                  className="form-input"
                  required
                  min="0"
                  step="1"
                  value={adjustCreditsAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d+$/.test(value)) {
                      setAdjustCreditsAmount(value);
                    }
                  }}
                  placeholder="Enter new credit amount"
                  autoFocus
                />
              </div>

              <div
                style={{
                  padding: '16px 0',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  marginTop: '16px',
                }}
              >
                <button
                  type="button"
                  className="tm-btn tm-btn-outline"
                  onClick={closeAdjustCreditsModal}
                >
                  Cancel
                </button>
                <button type="submit" className="tm-btn tm-btn-primary">
                  Update Credits
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Org Chart Modal */}
      {showOrgChartModal && selectedOrgForChart && (
        <OrgChartModal organization={selectedOrgForChart} onClose={closeOrgChartModal} />
      )}

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default OrganizationManagement;
